import React, { useState, useRef } from 'react';
import { Box, Typography, ToggleButtonGroup, ToggleButton } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { SectionCard, SectionHeader, SectionLoading } from './ui/Section';
import PillarMatrix from './PillarMatrix';
import { UI_FONT } from '../theme';
import { usePillarsPageData } from '../hooks/usePillarsPageData';
import { useFilterContext } from '../context/FilterContext';
import { format } from 'date-fns';
import { Chart as ChartJS, RadialLinearScale, PointElement, LineElement, Filler, Tooltip as ChartTooltip, Legend, ChartData, ChartOptions } from 'chart.js';
import { Radar } from 'react-chartjs-2';

// Register the required Chart.js components
ChartJS.register(RadialLinearScale, PointElement, LineElement, Filler, ChartTooltip);

interface BrandPillarsProps {
  selectedBrands: string[];
  selectedPillars: string[];
  startDate: string | null;
  endDate: string | null;
  hideLegend?: boolean;
  isPersonalPage?: boolean; // New prop to indicate this is personal page context
}

// Custom Legend component: click to focus a brand, hover to preview it
const CustomLegend = ({ brands, highlightedBrands, onBrandClick, onBrandHover, getBrandColor }: {
  brands: string[];
  highlightedBrands: Set<string>;
  onBrandClick: (brand: string) => void;
  onBrandHover: (brand: string | null) => void;
  getBrandColor: (brand: string) => string;
}) => {
  const focused = highlightedBrands.size > 0 && highlightedBrands.size < brands.length;
  return (
    <Box
      sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75, mb: 2 }}
      onMouseLeave={() => onBrandHover(null)}
    >
      {brands.map(brand => {
        const on = highlightedBrands.has(brand);
        const color = getBrandColor(brand);
        return (
          <Box
            key={brand}
            component="button"
            type="button"
            onClick={() => onBrandClick(brand)}
            onMouseEnter={() => onBrandHover(brand)}
            onFocus={() => onBrandHover(brand)}
            onBlur={() => onBrandHover(null)}
            aria-pressed={focused ? on : undefined}
            sx={(theme) => ({
              display: 'inline-flex',
              alignItems: 'center',
              gap: 0.75,
              height: 28,
              px: 1.25,
              borderRadius: '999px',
              border: `1px solid ${focused && on ? color : theme.palette.divider}`,
              bgcolor: focused && on ? `${color}1F` : 'background.paper',
              color: 'text.primary',
              font: 'inherit',
              fontSize: '0.8125rem',
              fontWeight: focused && on ? 600 : 500,
              cursor: 'pointer',
              opacity: focused && !on ? 0.55 : 1,
              transition: 'opacity 120ms ease, border-color 120ms ease, background-color 120ms ease',
              '&:hover': { opacity: 1, borderColor: color },
              '&:focus-visible': { outline: `2px solid ${theme.palette.primary.main}`, outlineOffset: 1 },
            })}
          >
            <Box component="span" sx={{ width: 9, height: 9, borderRadius: '50%', bgcolor: color, flexShrink: 0 }} />
            {brand}
          </Box>
        );
      })}
      {focused && (
        <Box
          component="button"
          type="button"
          onClick={() => onBrandClick('__all__')}
          sx={{
            height: 28,
            px: 1,
            border: 0,
            bgcolor: 'transparent',
            color: 'primary.main',
            font: 'inherit',
            fontSize: '0.8125rem',
            fontWeight: 600,
            cursor: 'pointer',
          }}
        >
          Show all
        </Box>
      )}
    </Box>
  );
};

const BrandPillars: React.FC<BrandPillarsProps> = ({
  selectedBrands: propSelectedBrands,
  selectedPillars: propSelectedPillars,
  startDate: propStartDate,
  endDate: propEndDate,
  hideLegend = false,
  isPersonalPage = false,
}) => {
  // State for the active engagement metric (0 = Average, 1 = Total)
  const [engagementMetric, setEngagementMetric] = useState<'average' | 'total'>('average');
  // Radar is the default view; the matrix is one click away for exact comparisons
  const [pillarView, setPillarView] = useState<'matrix' | 'radar'>('radar');
  const theme = useTheme();
  
  // State to track which brands are visible
  const [visibleBrands, setVisibleBrands] = useState<Set<string>>(new Set());
  
  // Keep track of which brands should be highlighted (all by default)
  const [highlightedBrands, setHighlightedBrands] = useState<Set<string>>(new Set());
  // Legend hover previews a single brand without changing the click selection
  const [hoveredBrand, setHoveredBrand] = useState<string | null>(null);
  
  // Use FilterContext to get the current filters
  const { 
    selectedBrands, 
    selectedPillars,
    selectedPlatforms,
    startDate: contextStartDate, 
    endDate: contextEndDate,
    brandColors,
    brandColorsLoading 
  } = useFilterContext();
  
  // Format dates for API
  const formattedStartDate = contextStartDate ? format(contextStartDate, 'yyyy-MM-dd') : propStartDate;
  const formattedEndDate = contextEndDate ? format(contextEndDate, 'yyyy-MM-dd') : propEndDate;
  
  // Function to get brand color with fallback
  const getBrandColor = (brand: string): string => {
    return brandColors[brand] || `hsl(${brand.length * 45}, 70%, 50%)`;
  };

  // Function to apply logarithmic transformation
  const applyLogTransformation = (value: number): number => {
    // Apply log(x) + 1 transformation to handle zero values and round to 2 decimals
    return Number(Math.log(Math.max(value, 0) + 1).toFixed(2));
  };

  // Log to debug
  console.log('BrandPillars using filters:', { 
    // Props
    propSelectedBrands, 
    propSelectedPillars,
    propStartDate,
    propEndDate,
    // Context
    contextBrands: selectedBrands,
    contextPillars: selectedPillars,
    contextPlatforms: selectedPlatforms,
    contextStartDate,
    contextEndDate,
    // Formatted
    formattedStartDate,
    formattedEndDate
  });
  
  // Use brands and pillars from context instead of props
  const brandsToUse = propSelectedBrands && propSelectedBrands.length > 0 ? propSelectedBrands : selectedBrands;
  const pillarsToUse = selectedPillars;
  const platformsToUse = selectedPlatforms;
  
  // Create API parameters
  const apiParams: any = {
    startDate: formattedStartDate,
    endDate: formattedEndDate,
  };

  // Add personal page context and brands info
  if (isPersonalPage) {
    apiParams.is_personal_page = 'true';
    if (brandsToUse.length > 0) {
      // Personal pages should only show a single person/brand
      apiParams.brands = [brandsToUse[0]];
    }
  } else {
    if (brandsToUse.length > 0) {
      // Support multi-brand selection for radar chart
      apiParams.brands = brandsToUse;

      // Maintain legacy single-brand parameter for other consumers when applicable
      if (brandsToUse.length === 1) {
        apiParams.brand = brandsToUse[0];
      }
    }
  }

  const { data, isLoading, error } = usePillarsPageData(apiParams);

  // Chart ref - removed specific type to avoid TypeScript errors
  const chartRef = useRef(null);

  // Reset highlighted brands when brands change. With many brands the radar
  // turns into a tangle, so start with the top five (by average engagement)
  // in focus and the rest as faint outlines; "Show all" brings them back.
  React.useEffect(() => {
    const metrics = data?.brand_pillar_metrics;
    if (!metrics || brandsToUse.length <= 6) {
      setHighlightedBrands(new Set(brandsToUse));
      return;
    }
    const overallAvg = (brand: string) => {
      let sum = 0;
      let posts = 0;
      Object.values(metrics[brand] || {}).forEach((m: any) => {
        sum += m?.sum_engagement || 0;
        posts += m?.post_count || 0;
      });
      return posts ? sum / posts : 0;
    };
    const top = [...brandsToUse].sort((a, b) => overallAvg(b) - overallAvg(a)).slice(0, 5);
    setHighlightedBrands(new Set(top));
  }, [brandsToUse, data]);

  // Handle legend click for brand highlighting
  const handleBrandClick = (brand: string) => {
    if (brand === '__all__') {
      setHighlightedBrands(new Set(brandsToUse));
      return;
    }
    setHighlightedBrands(prev => {
      const newSet = new Set(prev);
      
      // If this is the only brand highlighted, show all brands
      if (newSet.size === 1 && newSet.has(brand)) {
        brandsToUse.forEach(b => newSet.add(b));
      } 
      // If clicking on a brand that's not highlighted or along with others, show only this brand
      else if (!newSet.has(brand) || newSet.size > 1) {
        newSet.clear();
        newSet.add(brand);
      }
      
      return newSet;
    });
  };

  // Handle toggle change
  const handleMetricChange = (event: React.MouseEvent<HTMLElement>, newMetric: 'average' | 'total') => {
    if (newMetric !== null) {
      setEngagementMetric(newMetric);
    }
  };

  const sectionTitle = brandsToUse.length === 1
    ? `Performance Across Pillars for ${brandsToUse[0]}`
    : 'Performance Across Pillars';

  const metricToggle = (
    <ToggleButtonGroup
      value={engagementMetric}
      exclusive
      onChange={handleMetricChange}
      aria-label="engagement metric"
      size="small"
    >
      <ToggleButton value="average">Average Engagement</ToggleButton>
      <ToggleButton value="total">Total Engagement</ToggleButton>
    </ToggleButtonGroup>
  );

  // Handle brand visibility toggle
  const handleBrandToggle = (brand: string) => {
    setVisibleBrands(prev => {
      const newSet = new Set(prev);
      if (newSet.has(brand)) {
        newSet.delete(brand);
      } else {
        newSet.add(brand);
      }
      return newSet;
    });
  };

  // Get the list of brands to display based on visibility state
  const getVisibleBrands = () => {
    if (visibleBrands.size === 0) {
      return brandsToUse; // Show all brands if none are explicitly hidden
    }
    return brandsToUse.filter(brand => visibleBrands.has(brand));
  };

  // Function to filter and reaggregate data based on selected platforms
  const filterByPlatforms = (brandPillarMetrics: any) => {
    if (!brandPillarMetrics || !platformsToUse || platformsToUse.length === 0) {
      return brandPillarMetrics; // Return original data if no platforms selected
    }
    
    console.log('Filtering brand pillar metrics by platforms:', platformsToUse);
    
    // Create a new object for filtered metrics
    const filteredMetrics: Record<string, any> = {};
    
    // Process each brand
    Object.entries(brandPillarMetrics).forEach(([brand, brandData]: [string, any]) => {
      filteredMetrics[brand] = {};
      
      // Process each pillar for this brand
      Object.entries(brandData).forEach(([pillar, pillarData]: [string, any]) => {
        // Get platform data
        const platformData = pillarData.platforms || {};
        
        // Check if we need to filter (if we have platform data and selected platforms)
        if (Object.keys(platformData).length > 0 && platformsToUse.length > 0) {
          // Filter to only include selected platforms
          const filteredPlatforms = Object.entries(platformData)
            .filter(([platform]) => platformsToUse.includes(platform.toLowerCase()))
            .map(([_, data]) => data);
          
          // Skip if no platforms match the filter
          if (filteredPlatforms.length === 0) {
            // Use default values with zero counts
            filteredMetrics[brand][pillar] = {
              avg_engagement: 0,
              sum_engagement: 0,
              post_count: 0
            };
            return;
          }
          
          // Calculate new aggregated metrics across selected platforms
          let totalPosts = 0;
          let sumEngagement = 0;
          
          filteredPlatforms.forEach((platform: any) => {
            totalPosts += platform.post_count;
            sumEngagement += platform.sum_engagement;
          });
          
          // Calculate new average
          const avgEngagement = totalPosts > 0 ? sumEngagement / totalPosts : 0;
          
          // Store the reaggregated metrics
          filteredMetrics[brand][pillar] = {
            avg_engagement: avgEngagement,
            sum_engagement: sumEngagement,
            post_count: totalPosts
          };
        } else {
          // If no platform data or no platforms selected, use the original aggregated data
          filteredMetrics[brand][pillar] = pillarData;
        }
      });
    });
    
    console.log('Filtered metrics:', filteredMetrics);
    return filteredMetrics;
  };

  console.log('BrandPillars using brand filters:', brandsToUse);
  
  // If no brands are selected, show a message, but ensure we properly check the array
  if (!brandsToUse || brandsToUse.length === 0) {
    console.log('No brands selected, showing message');
    
    // Get available brands from the data
    let availableBrands: string[] = [];
    if (data && data.brand_pillar_metrics) {
      availableBrands = Object.keys(data.brand_pillar_metrics);
    }
    
    // If we have data but no selected brands, use the first 5 brands from the data
    if (availableBrands.length > 0 && data) {
      console.log('Using available brands instead:', availableBrands.slice(0, 5));
      
      // Use all available brands (up to 5) for the chart
      const defaultBrandsToUse = availableBrands.slice(0, 5);
      
      // Add explicit type guard for data to satisfy TypeScript
      const brandPillarMetrics = data?.brand_pillar_metrics || {};
      
      // Filter data by platforms
      const filteredBrandData = filterByPlatforms(brandPillarMetrics);
      
      // Filter data to include only these brands
      const filteredData = Object.entries(filteredBrandData)
        .filter(([brand]) => defaultBrandsToUse.includes(brand))
        .reduce((acc, [brand, metrics]) => {
          acc[brand] = metrics;
          return acc;
        }, {} as Record<string, any>);
      
      // Use only selected pillars or all pillars if none selected
      const pillarsForChart = pillarsToUse.length > 0 
        ? pillarsToUse 
        : (data?.pillars || []);
      
      console.log('Using pillars for chart:', pillarsForChart);
      
      // Transform data for radar chart based on selected engagement metric
      const chartData = {
        labels: pillarsForChart,
        datasets: defaultBrandsToUse.map(brand => {
          const brandColor = getBrandColor(brand);
          
          return {
            label: brand,
            data: pillarsForChart.map(pillar => {
              const brandMetrics = filteredData[brand];
              if (brandMetrics && brandMetrics[pillar]) {
                const rawValue = engagementMetric === 'average' 
                  ? brandMetrics[pillar].avg_engagement 
                  : brandMetrics[pillar].sum_engagement;
                
                return applyLogTransformation(rawValue);
              }
              return 0;
            }),
            backgroundColor: `${brandColor}33`, // Add transparency
            borderColor: brandColor,
            borderWidth: 2,
            pointBackgroundColor: brandColor,
            pointBorderColor: theme.palette.background.paper,
            pointHoverBackgroundColor: '#fff',
            pointHoverBorderColor: brandColor,
            pointRadius: 4,
            pointHoverRadius: 6,
          };
        })
      };
      
      const chartOptions = {
        scales: {
          r: {
            angleLines: {
              display: true,
              color: theme.palette.divider,
            },
            grid: {
              color: theme.palette.divider,
            },
            pointLabels: {
              font: {
                size: 12,
              },
              color: theme.palette.text.secondary,
            },
            ticks: {
              color: theme.palette.text.secondary,
              backdropColor: 'transparent',
            },
            suggestedMin: 0,
          },
        },
        plugins: {
          legend: {
            display: false, // Hide built-in legend, we'll use our custom one
          },
          tooltip: {
            callbacks: {
              label: (context: any) => {
                const value = context.raw;
                return `${context.dataset.label}: ${value.toFixed(2)}`;
              }
            }
          }
        },
        maintainAspectRatio: false,
      };
      
      // Return the Chart.js radar chart with custom legend
      return (
        <SectionCard>
          <SectionHeader
            title={sectionTitle}
            info="Radar chart comparing brand performance across content pillars. Click legend items to highlight specific brands. Log transformation applied for better visualization of different engagement scales."
            actions={metricToggle}
          />
          
          {!hideLegend && (
            <CustomLegend 
              brands={brandsToUse}
              highlightedBrands={highlightedBrands}
              onBrandClick={handleBrandClick}
              onBrandHover={setHoveredBrand}
              getBrandColor={getBrandColor}
            />
          )}
          
          <Box sx={{ height: '420px' }}>
            <Radar data={chartData} options={chartOptions} />
          </Box>
        </SectionCard>
      );
    }
    
    // If no brands are available in the data, show the message
    return (
      <SectionCard>
        <SectionHeader title={sectionTitle} />
        <Typography color="text.secondary" sx={{ py: 6, textAlign: 'center' }}>
          Select at least one brand to see pillar performance.
        </Typography>
      </SectionCard>
    );
  }

  // Show loading state while brand colors are loading
  if (brandColorsLoading) {
    return (
      <SectionCard>
        <SectionHeader title={sectionTitle} />
        <SectionLoading label="Loading brand colors..." height={400} />
      </SectionCard>
    );
  }

  // Show loading state
  if (isLoading) {
    return (
      <SectionCard>
        <SectionHeader title={sectionTitle} />
        <SectionLoading height={400} />
      </SectionCard>
    );
  }

  // Show error state
  if (error || !data) {
    return (
      <SectionCard>
        <SectionHeader title={sectionTitle} />
        <Typography color="error" sx={{ py: 6, textAlign: 'center' }}>
          Brand pillar data didn't load. Clear the data cache from the header and try again.
        </Typography>
      </SectionCard>
    );
  }
  
  console.log('BrandPillars data loaded:', { 
    brands: Object.keys(data.brand_pillar_metrics || {}),
    pillars: data.pillars,
    platforms: data.platforms
  });

  // Get platform-filtered data
  const filteredBrandPillarData = filterByPlatforms(data.brand_pillar_metrics || {});

  // Filter data to only include selected brands
  const filteredData = Object.entries(filteredBrandPillarData)
    .filter(([brand]) => brandsToUse.includes(brand))
    .reduce((acc, [brand, metrics]) => {
      acc[brand] = metrics;
      return acc;
    }, {} as Record<string, any>);
  
  console.log('Filtered brand data:', Object.keys(filteredData));

  // Use selected pillars or all pillars if none selected
  const pillarsForChart = pillarsToUse.length > 0 
    ? pillarsToUse 
    : (data.pillars || []);
    
  console.log('Using pillars for radar chart:', pillarsForChart);

  // Get visible brands
  const visibleBrandsArray = getVisibleBrands();

  // Define type for Chart.js data
  type RadarChartData = ChartData<'radar'>;
  
  // Find the maximum value across all brands for scaling
  const getMaxEngagementValue = () => {
    let maxValue = 0;
    
    // Check all brands and pillars to find the maximum value
    brandsToUse.forEach(brand => {
      const brandMetrics = filteredData[brand];
      if (brandMetrics) {
        pillarsForChart.forEach(pillar => {
          if (brandMetrics[pillar]) {
            const rawValue = engagementMetric === 'average' 
              ? brandMetrics[pillar].avg_engagement 
              : brandMetrics[pillar].sum_engagement;
            
            const transformedValue = applyLogTransformation(rawValue);
            maxValue = Math.max(maxValue, transformedValue);
          }
        });
      }
    });
    
    // Add a 10% buffer for better visualization
    return maxValue * 1.1;
  };
  
  const maxScaleValue = getMaxEngagementValue();
  
  // Hovering a legend item previews that brand on its own
  const activeBrands = hoveredBrand ? new Set([hoveredBrand]) : highlightedBrands;
  const isFocused = activeBrands.size > 0 && activeBrands.size < visibleBrandsArray.length;

  const metricValue = (brand: string, pillar: string) => {
    const m = filteredData[brand]?.[pillar];
    if (!m) return null;
    return engagementMetric === 'average' ? m.avg_engagement : m.sum_engagement;
  };

  // Prepare Chart.js data structure. Active brands are drawn last so they sit on top.
  const orderedBrands = [
    ...visibleBrandsArray.filter(b => !activeBrands.has(b)),
    ...visibleBrandsArray.filter(b => activeBrands.has(b)),
  ];
  const chartData: RadarChartData = {
    labels: pillarsForChart,
    datasets: orderedBrands.map(brand => {
      const brandColor = getBrandColor(brand);
      const active = activeBrands.has(brand);
      const solo = isFocused && active && activeBrands.size <= 2;

      return {
        label: brand,
        data: pillarsForChart.map(pillar => {
          const v = metricValue(brand, pillar);
          return v === null ? 0 : applyLogTransformation(v);
        }),
        backgroundColor: !active ? 'transparent' : solo ? `${brandColor}2E` : `${brandColor}12`,
        borderColor: active ? brandColor : `${brandColor}2A`,
        borderWidth: solo ? 2.5 : active ? 1.75 : 1,
        borderJoinStyle: 'round' as const,
        pointBackgroundColor: brandColor,
        pointBorderColor: theme.palette.background.paper,
        pointBorderWidth: 1.5,
        pointRadius: solo ? 3.5 : active ? 2.5 : 0,
        pointHoverRadius: active ? 5 : 0,
        pointHitRadius: active ? 8 : 0,
      };
    })
  };

  // Long pillar names wrap onto two lines around the radar
  const wrapLabel = (label: string): string | string[] => {
    if (label.length <= 18) return label;
    const words = label.split(' ');
    let first = '';
    while (words.length && (first + ' ' + words[0]).trim().length <= Math.ceil(label.length / 2) + 2) {
      first = (first + ' ' + words.shift()).trim();
    }
    return words.length ? [first, words.join(' ')] : label;
  };

  // Define type for Chart.js options
  type RadarChartOptions = ChartOptions<'radar'>;
  
  // Chart.js options
  const chartOptions: RadarChartOptions = {
    maintainAspectRatio: false,
    interaction: { mode: 'index', intersect: false },
    layout: { padding: 8 },
    scales: {
      r: {
        angleLines: {
          display: true,
          color: theme.palette.divider,
        },
        grid: {
          color: theme.palette.divider,
        },
        pointLabels: {
          font: { size: 12.5, family: UI_FONT, weight: 500 },
          color: theme.palette.text.secondary,
          padding: 10,
          callback: (label: string) => wrapLabel(label),
        },
        suggestedMin: 0,
        suggestedMax: maxScaleValue,
        // The radius is log-compressed, so its numbers mean nothing to a reader;
        // the tooltip shows the real values instead.
        ticks: { display: false, stepSize: Math.ceil(maxScaleValue / 5) },
      },
    },
    plugins: {
      legend: {
        display: false, // Hide built-in legend, we'll use our custom one
      },
      tooltip: {
        backgroundColor: theme.palette.background.paper,
        titleColor: theme.palette.text.primary,
        bodyColor: theme.palette.text.primary,
        borderColor: theme.palette.divider,
        borderWidth: 1,
        padding: 12,
        cornerRadius: 10,
        boxWidth: 8,
        boxHeight: 8,
        boxPadding: 5,
        usePointStyle: true,
        titleFont: { family: UI_FONT, size: 13, weight: 600 },
        bodyFont: { family: UI_FONT, size: 12.5 },
        titleMarginBottom: 8,
        filter: (item) => activeBrands.size === 0 || activeBrands.has(item.dataset.label as string),
        itemSort: (x, y) => (y.raw as number) - (x.raw as number),
        callbacks: {
          title: (items) => (items.length ? String(pillarsForChart[items[0].dataIndex]) : ''),
          label: (context) => {
            const v = metricValue(context.dataset.label as string, pillarsForChart[context.dataIndex]);
            const shown = v === null ? 'no posts' : Math.round(v).toLocaleString();
            return ` ${context.dataset.label}: ${shown}`;
          },
          labelPointStyle: () => ({ pointStyle: 'circle', rotation: 0 }),
          labelColor: (context) => ({
            borderColor: getBrandColor(context.dataset.label as string),
            backgroundColor: getBrandColor(context.dataset.label as string),
          }),
        },
      },
    },
  };

  return (
    <SectionCard>
      <SectionHeader
        title={sectionTitle}
        info={pillarView === 'matrix'
          ? "Each brand's engagement in each content pillar for the selected dates, shaded on one scale for the whole grid. A few cells show their numbers as reference points (turn on Show all values for every number), and a dot marks the leader of each pillar. Hover any cell for its details."
          : "Each brand's shape shows how its engagement spreads across content pillars for the selected dates. Hover a brand to preview it, click to focus it, and hover the chart for real numbers. The radius is compressed (log scale) so small and large brands fit on one chart."}
        actions={
          <>
            <ToggleButtonGroup
              value={pillarView}
              exclusive
              onChange={(_e, next: 'matrix' | 'radar' | null) => next && setPillarView(next)}
              aria-label="chart type"
              size="small"
            >
              <ToggleButton value="radar">Radar</ToggleButton>
              <ToggleButton value="matrix">Matrix</ToggleButton>
            </ToggleButtonGroup>
            {metricToggle}
          </>
        }
      />
      
      {pillarView === 'radar' && !hideLegend && (
        <CustomLegend 
          brands={brandsToUse}
          highlightedBrands={highlightedBrands}
          onBrandClick={handleBrandClick}
          onBrandHover={setHoveredBrand}
          getBrandColor={getBrandColor}
        />
      )}
      
      {pillarsForChart.length > 0 && pillarView === 'matrix' ? (
        <PillarMatrix
          data={filteredData}
          brands={brandsToUse.filter((b) => filteredData[b])}
          pillars={pillarsForChart}
          metric={engagementMetric}
          getBrandColor={getBrandColor}
        />
      ) : pillarsForChart.length > 0 ? (
        <Box sx={{ height: 480 }}>
          <Radar data={chartData} options={chartOptions} />
        </Box>
      ) : (
        <Typography color="text.secondary" sx={{ py: 6, textAlign: 'center' }}>
          No data for these brands, pillars, and platforms.
        </Typography>
      )}
    </SectionCard>
  );
};

export default BrandPillars; 